import React, { useEffect, useState } from 'react';

// ============================================================
// Landing page offer
// ============================================================
// Leads from the marketmapr.com/free-market form ("we'll map your first
// market free"). These are ordinary crm_prospects rows tagged
// source='free_market_landing', so they also live on the kanban — this
// tab is just a filtered, chronological triage view.

interface LandingLead {
  id: number;
  full_name: string;
  email: string | null;
  company: string | null;
  notes: string | null;
  stage: string;
  created_at: string;
}

const authHeaders = (): HeadersInit => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json'
});

// The intake endpoint writes notes as:
//   "Requested market: <text>\n\n(Submitted via ...)"
// Pull the market description back out for its own column; fall back to
// the raw notes if the format ever changes.
const extractMarket = (notes: string | null): string => {
  if (!notes) return '—';
  const m = notes.match(/^Requested market:\s*([\s\S]*?)\n\n\(Submitted via/);
  return m ? m[1].trim() : notes;
};

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) +
  ' · ' +
  new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

export const LandingOfferView: React.FC = () => {
  const [leads, setLeads] = useState<LandingLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/crm/landing-leads', { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to load landing leads');
      const data = await res.json();
      setLeads(data.leads || []);
      setError(null);
    } catch (e: any) {
      setError(e.message || 'Failed to load landing leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return <div className="py-12 text-center text-gray-500 text-sm">Loading landing-page leads…</div>;
  }
  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
        {error} <button onClick={load} className="underline ml-2">Retry</button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Landing page offer</h2>
          <p className="text-sm text-gray-500">
            Free-market-map interest from marketmapr.com/free-market — newest first.
            These leads also appear on the Pipeline kanban.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">{leads.length} lead{leads.length === 1 ? '' : 's'}</span>
          <button onClick={load} className="text-sm text-blue-600 hover:text-blue-800">↻ Refresh</button>
        </div>
      </div>

      {leads.length === 0 ? (
        <div className="py-16 text-center border border-dashed border-gray-300 rounded-xl">
          <p className="text-gray-500 text-sm">No submissions yet.</p>
          <p className="text-gray-400 text-xs mt-1">They'll appear here as soon as someone fills in the free-market form.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wide">
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Agency</th>
                <th className="px-4 py-3 font-semibold">Requested market</th>
                <th className="px-4 py-3 font-semibold">Submitted</th>
                <th className="px-4 py-3 font-semibold">Stage</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {leads.map(lead => (
                <tr key={lead.id} className="border-t border-gray-100 hover:bg-gray-50/60">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{lead.full_name}</div>
                    {lead.email && (
                      <a href={`mailto:${lead.email}`} className="text-xs text-blue-600 hover:underline">{lead.email}</a>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-700">{lead.company || '—'}</td>
                  <td className="px-4 py-3 text-gray-700 max-w-md">
                    <span className="line-clamp-3">{extractMarket(lead.notes)}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{formatDate(lead.created_at)}</td>
                  <td className="px-4 py-3">
                    <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-indigo-50 text-indigo-700 border border-indigo-100">
                      {lead.stage.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {lead.email && (
                      <a
                        href={`mailto:${lead.email}?subject=${encodeURIComponent('Your free market map — MarketMapr')}`}
                        className="text-xs px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 whitespace-nowrap"
                      >
                        Reply
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
